/**
 * Loads the art: each family's .glb (bundled with the app, made by `pnpm art:build`) and each
 * kind's model in it, checked against the contract. The models that keep it come back by kind;
 * what's wrong comes back in words, and those kinds keep their stand-ins. The glTF loader itself
 * loads only now, so it adds nothing to what the app loads at start. See docs/ART.md.
 */
import * as THREE from 'three';

import { ART_FAMILIES, ART_KINDS } from './manifest';
import { artProblems } from './validate';

/** Every family's .glb, as a URL the app can fetch (bundled and fingerprinted by Vite). */
const FILES = import.meta.glob<string>('./models/*.glb', {
  query: '?url',
  import: 'default',
  eager: true,
});

export interface Art {
  /** The models that keep the contract, by kind. */
  geometries: Map<string, THREE.BufferGeometry>;
  /** What's wrong, in words; empty when all is well. */
  problems: string[];
}

const isMesh = (o: THREE.Object3D): o is THREE.Mesh => o instanceof THREE.Mesh;

/** The mesh a kind's node holds: the node itself, or the first mesh inside it. */
function meshNamed(scene: THREE.Object3D, name: string): THREE.Mesh | null {
  const node = scene.getObjectByName(name);
  if (!node) return null;
  let found: THREE.Mesh | null = null;
  node.traverse((o) => {
    if (!found && isMesh(o)) found = o;
  });
  return found;
}

/** Positions, normals and three-channel colours, the node's placement baked in: what the props draw. */
function modelOf(mesh: THREE.Mesh): THREE.BufferGeometry {
  const geometry = mesh.geometry.clone().applyMatrix4(mesh.matrixWorld);
  for (const name of Object.keys(geometry.attributes))
    if (name !== 'position' && name !== 'normal' && name !== 'color')
      geometry.deleteAttribute(name);
  const color = geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
  if (color && color.itemSize !== 3) {
    const rgb = new Float32Array(color.count * 3);
    for (let i = 0; i < color.count; i++) {
      rgb[i * 3] = color.getX(i);
      rgb[i * 3 + 1] = color.getY(i);
      rgb[i * 3 + 2] = color.getZ(i);
    }
    geometry.setAttribute('color', new THREE.BufferAttribute(rgb, 3));
  }
  return geometry;
}

/**
 * Takes the contract's kinds out of a loaded scene and checks them, adding to `art`. With a
 * `family`, every kind of it must be there; without, whichever kinds the scene has.
 */
export function collectArt(scene: THREE.Object3D, art: Art, family?: string): void {
  scene.updateMatrixWorld(true);
  for (const [name, kind] of ART_KINDS) {
    if (family !== undefined && kind.family !== family) continue;
    const mesh = meshNamed(scene, name);
    if (!mesh) {
      if (family !== undefined) art.problems.push(`${name}: not in ${family}.glb`);
      continue;
    }
    const geometry = modelOf(mesh);
    const wrong = artProblems(name, kind, geometry);
    if (wrong.length > 0) art.problems.push(...wrong);
    else art.geometries.set(name, geometry);
  }
}

/** Loads every family the contract lists. */
export async function loadArt(): Promise<Art> {
  const art: Art = { geometries: new Map(), problems: [] };
  const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
  const loader = new GLTFLoader();
  for (const family of ART_FAMILIES.keys()) {
    const url = FILES[`./models/${family}.glb`];
    if (url === undefined) {
      art.problems.push(`${family}: no ${family}.glb yet (pnpm art:build makes it)`);
      continue;
    }
    try {
      collectArt((await loader.loadAsync(url)).scene, art, family);
    } catch (error) {
      art.problems.push(`${family}.glb: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  return art;
}

/** Reads a .glb that isn't bundled (dropped into the Tile Lab, or read by a test): whichever kinds it has. */
export async function parseArt(data: ArrayBuffer): Promise<Art> {
  const art: Art = { geometries: new Map(), problems: [] };
  const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
  collectArt((await new GLTFLoader().parseAsync(data, '')).scene, art);
  if (art.geometries.size === 0 && art.problems.length === 0)
    art.problems.push('no kind of the contract in this file');
  return art;
}
